/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { useMemo } from 'react';
import { Colors } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../../constants';
import { SubMenuHeading } from './SubMenuHeading';
import { SubMenuList } from './SubMenuList';
import { ICON_SIZE, HEADING_GAP } from './constants';
import type {
  NavItemVariantB,
  NavItemVariantC,
} from '../../../../constants/links';

type Props = {
  /**
   * Top-level headings
   * along with an optional description or list of links
   */
  links: UniversalNavLinkType[];
  /**
   * Variant to be rendered `navigationItem`'s `variant`.
   */
  variant: NavItemVariantB['variant'] | NavItemVariantC['variant'];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('MegaMenuSubmenu', {
  root: css({
    color: Colors.earth,
    display: 'grid',
    gap: '7%',
    gridTemplateColumns: 'repeat(3, minmax(0px, max-content))',
    gridTemplateRows: 'auto',
    height: '100%',
  }),
  groupContainer: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '35px',
  }),
  group: css({
    color: Colors.inherit,
    '&:hover, &:focus, &:focus-within': {
      color: Colors.zapier,
    },
  }),

  listWrapper: (hasIcon: boolean) =>
    css({
      paddingLeft: hasIcon ? `${ICON_SIZE + HEADING_GAP}px` : 0,
    }),
});

/**
 * @private
 * Group of links inside the MegaMenu
 */
export const MegaMenuSubmenu = (props: Props) => {
  const { links, variant } = props;

  // Organizes the links for variant-c into
  // an array with a nested array for the third column
  const linksWithMaybeSubcolumn = useMemo(() => {
    return variant === 'variant-c' && links.length > 3
      ? [...links.slice(0, 2), links.slice(2)]
      : links;
  }, [links]);

  return (
    <ul css={Styles.root}>
      {linksWithMaybeSubcolumn.map((item) =>
        Array.isArray(item) ? (
          <li key={variant}>
            <ul css={Styles.groupContainer}>
              {item.map((submenulink) => (
                <li key={submenulink.text} css={Styles.group}>
                  <SubMenuHeading
                    link={submenulink}
                    onCloseMenu={props.onCloseMenu}
                  />
                  {submenulink.submenu && (
                    <div css={Styles.listWrapper(!!submenulink.icon)}>
                      <SubMenuList
                        submenu={submenulink.submenu}
                        onCloseMenu={props.onCloseMenu}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ) : (
          <li key={item.text} css={Styles.group}>
            <div role={item.submenu ? 'group' : 'presentation'}>
              <SubMenuHeading link={item} onCloseMenu={props.onCloseMenu} />
              {item.submenu && (
                <div css={Styles.listWrapper(!!item.icon)}>
                  <SubMenuList
                    submenu={item.submenu}
                    onCloseMenu={props.onCloseMenu}
                  />
                </div>
              )}
            </div>
          </li>
        )
      )}
    </ul>
  );
};
